import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  BarChart3, Package, ShoppingCart, Settings, Bell, 
  Search, Menu, X, Home, TrendingUp, User,
  DollarSign, MessageSquare, HelpCircle, LogOut, Video, AlertTriangle,
  ChevronDown, Star, Eye, Plus
} from 'lucide-react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { supabase } from '../../lib/supabase';

interface SellerLayoutProps {
  children: React.ReactNode;
}

const SellerLayout: React.FC<SellerLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useSupabase();
  const { user } = useSupabase();
  const [supplierData, setSupplierData] = useState<any>(null);

  // Fetch supplier data
  React.useEffect(() => {
    const fetchSupplierData = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from('suppliers')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        
        if (error) throw error;
        setSupplierData(data);
      } catch (err) {
        console.error('Error fetching supplier data:', err);
      }
    };
    
    fetchSupplierData();
  }, [user]);

  // Sample notifications data
  const notifications = [
    {
      id: 1,
      title: 'New Order Received',
      message: 'You have received a new order #ORD-001',
      time: '5 minutes ago',
      read: false,
      type: 'order'
    },
    {
      id: 2,
      title: 'Product Approved',
      message: 'Your product "Cotton T-Shirt" has been approved',
      time: '1 hour ago',
      read: false,
      type: 'product'
    },
    {
      id: 3,
      title: 'Payment Processed',
      message: 'Payment of $150.00 has been processed',
      time: '2 hours ago',
      read: true,
      type: 'payment'
    },
    {
      id: 4,
      title: 'Low Stock Alert',
      message: 'Cotton Polo Shirt is running low on stock',
      time: '3 hours ago',
      read: true,
      type: 'alert'
    },
    {
      id: 5,
      title: 'Profile Updated',
      message: 'Your supplier profile has been updated successfully',
      time: '1 day ago',
      read: true,
      type: 'profile'
    }
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <ShoppingCart className="h-4 w-4 text-blue-600" />;
      case 'product':
        return <Package className="h-4 w-4 text-green-600" />;
      case 'payment':
        return <DollarSign className="h-4 w-4 text-purple-600" />;
      case 'alert':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'profile':
        return <User className="h-4 w-4 text-gray-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const markAsRead = (notificationId: number) => {
    // In a real app, this would update the notification status in the database
    console.log('Mark notification as read:', notificationId);
  };

  const markAllAsRead = () => {
    // In a real app, this would mark all notifications as read in the database
    console.log('Mark all notifications as read');
  };
  const navigation = [
    { name: 'Dashboard', href: '/seller-central', icon: BarChart3, current: location.pathname === '/seller-central' },
    { name: 'Products', href: '/seller-central/products', icon: Package, current: location.pathname === '/seller-central/products' },
    { name: 'Add Product', href: '/seller-central/products/add', icon: Plus, current: location.pathname === '/seller-central/products/add' },
    { name: 'Videos', href: '/seller-central/videos', icon: Video, current: location.pathname === '/seller-central/videos' },
    { name: 'Orders', href: '/seller-central/orders', icon: ShoppingCart, current: location.pathname === '/seller-central/orders' },
    { name: 'Inventory', href: '/seller-central/inventory', icon: Package, current: location.pathname === '/seller-central/inventory' },
    { name: 'Analytics', href: '/seller-central/analytics', icon: TrendingUp, current: location.pathname === '/seller-central/analytics' },
    { name: 'Profile', href: '/seller-central/profile', icon: User, current: location.pathname === '/seller-central/profile' },
    { name: 'Support', href: '/seller-central/support', icon: MessageSquare, current: location.pathname === '/seller-central/support' },
    { name: 'Settings', href: '/seller-central/settings', icon: Settings, current: location.pathname === '/seller-central/settings' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      // Always redirect to home page regardless of sign out success/failure
      navigate('/');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <Link to="/" className="flex items-center">
            <img 
              src="/madeinbd-logo.png" 
              alt="Made in BD" 
              className="h-8 w-auto object-contain"
            />
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-gray-200">
          <div className="text-sm font-medium text-gray-900">Seller Central</div>
          <div className="text-xs text-gray-500">{supplierData?.organization || 'Loading...'}</div>
        </div>

        <nav className="mt-6 px-3">
          <div className="space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  item.current
                    ? 'bg-orange-100 text-orange-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${
                  item.current ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                }`} />
                {item.name}
              </Link>
            ))}
          </div>
        </nav>

        {/* Quick Actions */}
        <div className="mt-8 px-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Quick Actions</h3>
          <div className="mt-3 space-y-2">
            <Link
              to="/seller-central/products/add"
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Link>
            <Link
              to="/seller-central/orders"
              className="flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Orders
            </Link>
          </div>
        </div>

        {/* Performance Summary */}
        <div className="mt-8 px-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Performance</h3>
          <div className="mt-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Rating</span>
              <div className="flex items-center">
                <Star className="h-3 w-3 text-yellow-400 fill-current mr-1" />
                <span className="text-sm font-medium text-gray-900">4.8</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Orders</span>
              <span className="text-sm font-medium text-gray-900">156</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Revenue</span>
              <span className="text-sm font-medium text-green-600">$45.6K</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-500 hover:text-gray-700"
              >
                <Menu className="h-6 w-6" />
              </button>
              <div className="hidden md:block ml-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search products, orders..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <button 
                  onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                  className="text-gray-500 hover:text-gray-700 relative"
                >
                  <Bell className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {notificationDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                    
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center">
                          <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500">No notifications yet</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200">
                          {notifications.map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => markAsRead(notification.id)}
                              className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                !notification.read ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 mt-1">
                                  {getNotificationIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between">
                                    <p className={`text-sm font-medium ${
                                      !notification.read ? 'text-gray-900' : 'text-gray-700'
                                    }`}>
                                      {notification.title}
                                    </p>
                                    {!notification.read && (
                                      <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {notification.time}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4 border-t border-gray-200">
                      <Link
                        to="/seller-central/notifications"
                        onClick={() => setNotificationDropdownOpen(false)}
                        className="block w-full text-center bg-orange-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors"
                      >
                        View All Notifications
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Profile dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <img
                    className="h-8 w-8 rounded-full object-cover"
                    src="https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg"
                    alt="Seller"
                  />
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-medium text-gray-900">{supplierData?.organization || 'Loading...'}</div>
                    <div className="text-xs text-gray-500">{supplierData?.verified ? 'Verified Seller' : 'Pending Verification'}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </button>

                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      <Link
                        to="/seller-central/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <User className="h-4 w-4 mr-3" />
                        Profile
                      </Link>
                      <Link
                        to="/seller-central/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Settings className="h-4 w-4 mr-3" />
                        Settings
                      </Link>
                      <Link
                        to="/seller-central/help"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <HelpCircle className="h-4 w-4 mr-3" />
                        Help
                      </Link>
                      <div className="border-t border-gray-100">
                        <button 
                          onClick={handleSignOut}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
        
        {/* Click outside to close dropdowns */}
        {(notificationDropdownOpen || profileDropdownOpen) && (
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => {
              setNotificationDropdownOpen(false);
              setProfileDropdownOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SellerLayout;